import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { post, get } from 'store/request';
import { message } from '@tencent/spaui';
import { OperatorRole } from 'model/operator';

export const fetchUser = createAsyncThunk('user/fetchUser', (ignoreAppReq: boolean) =>
  Promise.all([
    get('/user').catch((e) => e),
    ...(ignoreAppReq ? [] : [get('/my_applications').catch((e) => e), get('/user/accounts').catch((e) => e)]),
  ])
);

export const login = createAsyncThunk(
  'user/login',
  async ({ ticket, ignoreAppReq }: { ticket: string; ignoreAppReq: boolean }) => {
    const user = await post('/login', { sso_ticket: ticket }).catch((e) => e);
    if (ignoreAppReq) {
      return [user];
    }

    const userInfo = await get('/user').catch((e) => e);
    const myApp = await get('/my_applications').catch((e) => e);
    const roles = await get('/user/accounts').catch((e) => e);
    // merge /user 和 /login 里信息，相同字段以 /user 为主
    return [{ ...user, ...userInfo }, myApp, roles];
  }
);

export const logout = createAsyncThunk('user/logout', () => post('/logout'));

export const fetchAccounts = createAsyncThunk('user/fetchAccounts', (id: string) =>
  get(`/developer_service/accounts?client_id=${id}`)
);

interface SuperAdminAccounts {
  account_id: Number;
  account_type: Number;
  enterprise_name: String;
}

export interface User {
  user_id: number;
  login_type: number; // 当前登录类型，1-QQ，2-微信
  active_account_role: number;
  login_name_type: number;
  qq_number: string;
  qq_nick_name: string;
  qq_figure_url_40_40: string;
  wx_id: string;
  wx_nick_name: string;
  wx_head_url: string;
  g_tk: string;
  // 关联账户信息
  accounts_info: {
    account_id: string;
    role: number;
    client_ids: string;
    // 1-直客，2-代理商
    account_type: 1 | 2;
    login_name: string;
  }[];
  super_admin_accounts: SuperAdminAccounts[];
  loading: boolean;
  auto_activation_auth_status: boolean; // 当前登录用户是否做过一键授权
  auto_activation_authed_status: boolean; // 当前登录用户关联的广告账户是否被做过一键授权
  agency_role: OperatorRole;
  agency_id: number;
  operator_id: number;
  operator_name: string;
  active_account_type: number;
}

export interface MyApplication {
  client_id: string;
  app_name: string;
  app_icon: string;
  // 0 所有未开通，1 一部分开通，2 全部开通
  authorize_status: 0 | 1 | 2;
  // 0 有超管但未开通，1 有超管部分开通，2 无超管或全部开通
  super_admin_status: 0 | 1 | 2;
  no_authorize: number;
  client_bind_status?: boolean; // 应用是否被绑定到服务商 true 绑定了， false 没绑定
  authorize_account_count: string | number; // 开通应用数量
}

export interface Account {
  role_name: string;
  account_id: string;
  enterprise_name: string;
  main_account_id: string;
  account_type: number;
  nick_name: string;
  qq: string;
  wechat: string;
  spid: string;
}

const initialState = {
  loading: true,
  user: null as User,
  myApp: [] as MyApplication[],
  roles: [] as number[],
  accounts: [] as Account[],
  lastSelected: [] as string[],
  accountsLoading: false,
  auto_activation_auth_status: false, // 当前登录用户是否做过一键授权
  auto_activation_authed_status: false, // 当前登录用户关联的广告账户是否被做过一键授权
  accountList: null as null | any, // 当前用户的角色列表;
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAuthBindStatus: (state, { payload }) => {
      const index = state.myApp.findIndex(({ client_id }) => client_id === payload.id);
      state.myApp[index].client_bind_status = payload.client_bind_status;
    },
    setAuthStatus: (state, { payload }) => {
      const index = state.myApp.findIndex(({ client_id }) => client_id === payload.id);
      state.myApp[index].authorize_status = payload.authorize_status;
      state.myApp[index].super_admin_status = payload.super_admin_status;
      state.myApp[index].authorize_account_count = payload.authorize_account_count;
    },
    updateUserAuthStatus: (state, { payload }) => {
      state.user.auto_activation_auth_status = payload;
      // state.user.auto_activation_authed_status = true;
    },
  },
  extraReducers: (builder) => {
    [fetchUser, login].forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => {
        state.loading = true;
      });
      builder.addCase(thunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        const userInfo = payload[0];
        if (!userInfo) {
          return;
        }

        // eslint-disable-next-line prefer-destructuring
        state.user = userInfo;
        state.myApp = Array.isArray(payload?.[1]?.list) ? payload?.[1]?.list : [];
        state.roles = Array.isArray(payload?.[2])
          ? Array.from(new Set(payload?.[2]?.map((item: any) => item.role)))
          : [];

        // 1. 用户没登陆；2. 用户登陆后 但是没有选择身份； 走登陆接口
        // 登陆后用户的 active_account_type 为 1 2 15 一定不为 0
        if (!payload[0]?.active_account_type && !payload[1] && !payload[2]) {
          state.accountList = userInfo.account_list;
        }
      });
    });

    builder.addCase(logout.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.loading = false;
      state.user = null as User;
    });
    builder.addCase(logout.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(fetchAccounts.pending, (state) => {
      state.accountsLoading = true;
    });
    builder.addCase(fetchAccounts.fulfilled, (state, { payload }) => {
      if (!payload?.list?.length) {
        // message.error('无可用账户信息');
        message.error({
          duration: 0,
          message: '无可用账号信息，请点击右上角【管理中心-应用查看】检查工具开通状态，根据页面提示进行工具开通操作。',
          closable: true,
        });
      }

      // 根据主体名称排序
      const map = new Map<string, Account[]>();
      payload?.list?.forEach((item: Account & { enterprise: { enterprise_name: string } }) => {
        const enterprise = item.enterprise.enterprise_name;
        const account = { ...item, enterprise_name: enterprise };
        if (!map.has(enterprise)) {
          map.set(enterprise, [account]);
        } else {
          map.set(enterprise, map.get(enterprise).concat(account));
        }
      });
      const accounts: Account[] = [];
      for (const value of map.values()) {
        accounts.push(...value);
      }
      state.accounts = accounts;

      state.lastSelected = payload?.selected;
      state.accountsLoading = false;
    });
    builder.addCase(fetchAccounts.rejected, (state) => {
      state.accountsLoading = false;
    });
  },
});

export const { setAuthStatus, updateUserAuthStatus, setAuthBindStatus } = userSlice.actions;

export default userSlice;
